import type { MeetingTranscript } from '@live-stream/shared'

/**
 * A meeting's transcript on the page (Phase 10), pure: lines gathered into
 * turns — one person speaking until someone else does, or a long pause —
 * and the meeting's clock laid over a recording's.
 */

type Segment = MeetingTranscript['segments'][number]
type Recording = MeetingTranscript['recordings'][number]

export interface Turn {
  speakerKey: string
  speakerName: string
  startMs: number
  endMs: number
  lines: Segment[]
}

/** A pause this long starts a new turn even when the same person goes on. */
export const TURN_GAP_MS = 20_000

export function turns(segments: readonly Segment[], gapMs = TURN_GAP_MS): Turn[] {
  const out: Turn[] = []
  for (const s of segments) {
    const last = out[out.length - 1]
    if (last && last.speakerKey === s.speakerKey && s.startMs - last.endMs < gapMs) {
      last.lines.push(s)
      last.endMs = Math.max(last.endMs, s.endMs)
    } else {
      out.push({
        speakerKey: s.speakerKey,
        speakerName: s.speakerName,
        startMs: s.startMs,
        endMs: s.endMs,
        lines: [s],
      })
    }
  }
  return out
}

/** The index of the line being spoken at `ms` into the meeting, or the one just before; -1 before any. */
export function lineAt(lines: readonly Segment[], ms: number): number {
  let lo = 0
  let hi = lines.length - 1
  let found = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (lines[mid]!.startMs <= ms) {
      found = mid
      lo = mid + 1
    } else hi = mid - 1
  }
  return found
}

/** The recording to play: the first one ready, when there is one. */
export const playable = (recordings: readonly Recording[]): Recording | null =>
  recordings.find((r) => r.status === 'ready') ?? null

/** Where in `recording` the meeting's moment `ms` is, in seconds; null when it was not recording then. */
export function recordingSecondsAt(recording: Recording, ms: number): number | null {
  const into = ms - recording.offsetMs
  if (into < 0) return null
  if (recording.durationMs !== null && into > recording.durationMs) return null
  return into / 1000
}

/** The meeting's moment for a recording's playhead. */
export const meetingMsAt = (recording: Recording, seconds: number): number =>
  recording.offsetMs + seconds * 1000

/**
 * The moment a link points at (`?t=<ms>`, as every time in an exported or
 * sent-on set of notes does), or null when it names none. A bare number is
 * ms; `90s` or `1m30s` are read too, as people type them.
 */
export function momentFromQuery(t: unknown): number | null {
  const raw = Array.isArray(t) ? t[0] : t
  if (typeof raw !== 'string' || !raw.trim()) return null
  const s = raw.trim()
  if (/^\d+$/.test(s)) return Number(s)
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(s)
  if (!m || (!m[1] && !m[2] && !m[3])) return null
  return ((Number(m[1] ?? 0) * 60 + Number(m[2] ?? 0)) * 60 + Number(m[3] ?? 0)) * 1000
}

/** The whole transcript as plain text: a paragraph per turn, the speaker first. */
export function asText(all: readonly Turn[], clock: (ms: number) => string): string {
  return all
    .map((t) => `${t.speakerName} (${clock(t.startMs)}): ${t.lines.map((l) => l.text).join(' ')}`)
    .join('\n\n')
}
